/* eslint-disable react/prop-types */
import { InputLabel, MenuItem } from '@material-ui/core'
import { Field, useFormikContext } from 'formik'
import { get } from 'lodash'
import React from 'react'

import { Select, TextField } from 'app/components/generic/FormElements'
import CloseIcon from 'assets/images/close.svg'

import {
  Container,
  Typography,
  RedirectCTA,
  Button,
  FormControl,
  FieldGroup,
  FieldContainer,
  Icon,
  FormHelperText
} from './styles'

import CloudFileUploader from '../CloudFileUploader'
import WYSIWYG from '../generic/WYSIWYG'
import InteractionTemplate from '../InteractionTemplate'
import VoiceCapture from '../VoiceCapture'

// Plain spoken text → safe HTML for the WYSIWYG editor (which stores HTML).
const transcriptToHtml = (text) =>
  (text || '')
    .split(/\n+/)
    .map((line) =>
      `<p>${line
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')}</p>`
    )
    .join('')

const NestedFieldWidget = ({ fieldData, ...props }) => {
  const { config, setFieldValue, formikFieldData, fieldName } = props

  // Bump per-row to force the WYSIWYG to remount after voice fills it — the
  // editor only reads its value on mount, so a plain setFieldValue wouldn't
  // show the transcribed text.
  const [voiceVersions, setVoiceVersions] = React.useState({})
  // Latest rows, so a voice result written after several re-renders merges into
  // current form state instead of a stale copy.
  const formikDataRef = React.useRef(formikFieldData)
  formikDataRef.current = formikFieldData

  const maxItems = config?.maxItems
  const isOptional = config?.optional

  const fieldGroup = config.fields.reduce((acc, field) => {
    acc[field.name] = field.initialValue || ''
    return acc
  }, {})

  // On mount, initialize if needed
  React.useEffect(() => {
    if (!formikFieldData) {
      setFieldValue(fieldName, isOptional ? [] : [fieldGroup])
    }
    // eslint-disable-next-line
  }, [])

  const addField = () => {
    const current = formikFieldData || []
    if (!maxItems || current.length < maxItems) {
      setFieldValue(fieldName, [...current, { ...fieldGroup }])
    }
  }

  const removeField = (index) => {
    const current = formikFieldData || []
    const newData = [...current]
    newData.splice(index, 1)
    setFieldValue(fieldName, newData)
  }

  const handleFileUploadOnChange = (uploadData, index, fieldPath) => {
    const { data } = uploadData
    const current = formikFieldData || []
    const newData = [...current]
    newData[index] = { ...newData[index], [fieldPath.split('.').pop()]: data }
    setFieldValue(fieldName, newData)
  }

  const { errors, validateField } = useFormikContext()
  const formElement = React.useMemo(() => {
    const current = formikFieldData || []
    return (
      <Container>
        {config.widgetTitle && (
          <Typography variant="h5" $css={`margin-bottom: 15px;`}>
            {config.widgetTitle}
          </Typography>
        )}

        {current.map((field, index) => (
          <React.Fragment key={index}>
            {config?.headingWithIndex && (
              <Typography
                variant="subtitle1"
                $css={`margin-bottom: 12px; font-weight: 600; color: #00000850; font-size:14px; `}>
                {`${config.headingWithIndex} ${index + 1}`}
              </Typography>
            )}
            <FieldContainer>
              <FieldGroup>
                {config.fields.map((fieldConfig) => {
                  const fieldPath = `${fieldName}[${index}].${fieldConfig.name}`
                  const fieldError = get(errors, fieldPath)

                  if (fieldConfig.type === 'select') {
                    return (
                      <FormControl fullWidth variant="outlined" key={fieldPath}>
                        <InputLabel>{fieldConfig.label}</InputLabel>
                        <Select
                          name={fieldPath}
                          label={fieldConfig.label}
                          value={field[fieldConfig.name]}
                          onChange={(e) => {
                            const current = formikFieldData || []
                            const newData = [...current]
                            newData[index] = {
                              ...newData[index],
                              [fieldConfig.name]: e.target.value
                            }
                            setFieldValue(fieldName, newData)
                            validateField(fieldPath)
                          }}
                          {...(typeof fieldConfig.validate === 'function'
                            ? {
                                validate: fieldConfig.validate({
                                  isRequired: !fieldConfig?.optional
                                })
                              }
                            : null)}
                          required={!fieldConfig?.optional}
                          error={Boolean(fieldError)}
                          helperText={fieldError ? fieldError : ''}>
                          {Object.entries(fieldConfig.valueLabelMappings)
                            // `activeValues` (optional): only these are offered;
                            // any other mapped value shows only on the row that
                            // already holds it, disabled — so a retired option
                            // on an old record still displays but can't be
                            // picked anew (interaction topics, user 2026-09-24).
                            .filter(
                              ([value]) =>
                                !fieldConfig.activeValues ||
                                fieldConfig.activeValues.includes(value) ||
                                value === field[fieldConfig.name]
                            )
                            .filter(([value, label]) =>
                              current.every((field, idx) => {
                                // Filter out if this value is already selected in another field
                                if (idx !== index) {
                                  return fieldConfig.uniqueOption === true //Filtering happens only when the field has uniqueConfig set to true
                                    ? field[fieldConfig.name] !== value
                                    : true
                                }
                                return true
                              })
                            )
                            .map(([value, label], idx) => (
                              <MenuItem
                                key={idx}
                                value={value}
                                disabled={
                                  Boolean(fieldConfig.activeValues) &&
                                  !fieldConfig.activeValues.includes(value)
                                }>
                                {label}
                              </MenuItem>
                            ))}
                        </Select>
                        {fieldError && (
                          <FormHelperText>{fieldError}</FormHelperText>
                        )}
                      </FormControl>
                    )
                  } else if (fieldConfig.type === 'fileupload') {
                    return (
                      <FormControl
                        variant="outlined"
                        key={fieldPath}
                        required={!fieldConfig?.optional}
                        fullWidth>
                        <Field
                          name={fieldPath}
                          validate={
                            typeof fieldConfig.validate === 'function'
                              ? fieldConfig.validate({
                                  isRequired: !fieldConfig.optional
                                })
                              : undefined
                          }
                          component={({
                            field: formikField,
                            form,
                            ...props
                          }) => (
                            <>
                              <CloudFileUploader
                                {...formikField}
                                {...props}
                                {...form}
                                fileUrlOnly={fieldConfig.fileUrlOnly}
                                name={fieldPath}
                                label={fieldConfig.label}
                                onUpload={(data) =>
                                  handleFileUploadOnChange(
                                    data,
                                    index,
                                    fieldPath
                                  )
                                }
                                uploadedFileUrl={
                                  get(
                                    formikFieldData,
                                    `[${index}].${fieldConfig.name}`
                                  )?.url
                                }
                                uploadPreset={fieldConfig.uploadPreset}
                                useNewMediaUpload={
                                  fieldConfig.newMedia || false
                                }
                              />
                              <FormHelperText>
                                {get(form.errors, fieldPath)}
                              </FormHelperText>
                            </>
                          )}
                        />
                      </FormControl>
                    )
                  } else if (fieldConfig.type === 'wysiwyg') {
                    // Interaction TEMPLATE injection (gated on config.templates,
                    // which only the interaction form passes → Subjects pages
                    // are unaffected). When the row's topic has a template,
                    // render it in place of the free-text editor; it writes both
                    // structured_data (JSON) and the summary (auto-rendered) onto
                    // the row.
                    const template =
                      config.templates &&
                      field.topic &&
                      config.templates[field.topic]
                    if (template) {
                      return (
                        <FormControl
                          variant="outlined"
                          key={`${fieldPath}-${field.topic}`}
                          fullWidth>
                          <Typography variant="caption">
                            {fieldConfig.label}
                          </Typography>
                          <InteractionTemplate
                            template={template}
                            topic={field.topic}
                            data={field.structured_data || {}}
                            subjectOptions={config.subjectOptions || []}
                            onChange={(structured, summaryHtml) => {
                              const current = formikFieldData || []
                              const newData = [...current]
                              newData[index] = {
                                ...newData[index],
                                structured_data: structured,
                                // keep summary non-blank (backend requires it)
                                [fieldConfig.name]: summaryHtml || field.topic
                              }
                              setFieldValue(fieldName, newData)
                            }}
                          />
                        </FormControl>
                      )
                    }
                    // Free-text (non-templated) topic. On the interaction form
                    // (which passes config.templates) offer a mic that dictates
                    // the note; the transcript replaces the editor contents,
                    // which the team then edits before saving.
                    const showVoice = Boolean(config.templates)
                    const onFreeTextVoice = (result) => {
                      const transcript = (result && result.transcript) || ''
                      if (!transcript) return
                      const current = formikDataRef.current || []
                      const newData = [...current]
                      newData[index] = {
                        ...newData[index],
                        [fieldConfig.name]: transcriptToHtml(transcript)
                      }
                      setFieldValue(fieldName, newData)
                      setVoiceVersions((v) => ({
                        ...v,
                        [index]: (v[index] || 0) + 1
                      }))
                    }
                    return (
                      <FormControl
                        variant="outlined"
                        key={fieldPath}
                        required={!fieldConfig.optional}
                        fullWidth>
                        <div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 10,
                              flexWrap: 'wrap',
                              marginBottom: 4
                            }}>
                            <Typography variant="caption">
                              {fieldConfig.label}
                              {!fieldConfig.optional ? '*' : ''}
                            </Typography>
                            {showVoice && (
                              <VoiceCapture
                                topic={field.topic}
                                fields={[]}
                                onResult={onFreeTextVoice}
                                tooltip="Speak the note — Alfred types it out"
                              />
                            )}
                          </div>
                          <Field
                            name={fieldPath}
                            validate={
                              typeof fieldConfig.validate === 'function'
                                ? fieldConfig.validate({
                                    isRequired: !fieldConfig.optional
                                  })
                                : undefined
                            }>
                            {({ field: formikField, form }) => (
                              <>
                                <WYSIWYG
                                  key={`wys-${index}-${voiceVersions[index] || 0}`}
                                  field={formikField}
                                  form={form}
                                />
                                {fieldError && (
                                  <FormHelperText>{fieldError}</FormHelperText>
                                )}
                              </>
                            )}
                          </Field>
                        </div>
                      </FormControl>
                    )
                  } else {
                    return (
                      <FormControl fullWidth variant="outlined" key={fieldPath}>
                        <Field
                          name={fieldPath}
                          validate={
                            typeof fieldConfig.validate === 'function'
                              ? fieldConfig.validate({
                                  isRequired: !fieldConfig?.optional
                                })
                              : undefined
                          }>
                          {({ field: formikField, form }) => (
                            <>
                              <TextField
                                {...formikField}
                                required={!fieldConfig?.optional}
                                variant="outlined"
                                label={fieldConfig.label}
                                error={Boolean(
                                  get(form.errors, fieldPath) &&
                                    get(form.touched, fieldPath)
                                )}
                                helperText={
                                  get(form.errors, fieldPath) &&
                                  get(form.touched, fieldPath)
                                    ? get(form.errors, fieldPath)
                                    : ''
                                }
                                {...(fieldConfig.maxCharacters
                                  ? {
                                      inputProps: {
                                        ...{
                                          maxLength: fieldConfig.maxCharacters
                                        }
                                      }
                                    }
                                  : null)}
                              />
                            </>
                          )}
                        </Field>
                      </FormControl>
                    )
                  }
                })}
              </FieldGroup>
              {/* If more than one field, then show close icon */}
              {isOptional || current.length > 1 ? (
                <Icon
                  src={CloseIcon}
                  alt="close"
                  onClick={() => removeField(index)}
                />
              ) : null}
            </FieldContainer>
          </React.Fragment>
        ))}

        <RedirectCTA>
          {(!maxItems || (formikFieldData || []).length < maxItems) && (
            <Button variant="outlined" onClick={addField}>
              {config.ctaLabel}
            </Button>
          )}
        </RedirectCTA>
      </Container>
    )
  }, [
    config,
    setFieldValue,
    formikFieldData,
    fieldName,
    errors,
    validateField,
    isOptional,
    maxItems,
    voiceVersions
  ])

  return <>{formElement}</>
}

export default NestedFieldWidget
